import { useEffect, useState, RefObject } from "react";

interface TextSelection {
  start: number;
  end: number;
  text: string;
}

interface Comment {
  id: string;
  color: string;
  textSelections: TextSelection[];
  isGeneral: boolean;
}

interface ConnectorLinesProps {
  comments: Comment[];
  editorRef: RefObject<HTMLDivElement>;
  panelRef: RefObject<HTMLDivElement>;
}

export const ConnectorLines = ({ comments, editorRef, panelRef }: ConnectorLinesProps) => {
  const [lines, setLines] = useState<Array<{
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    color: string;
  }>>([]);

  useEffect(() => {
    const calculateLines = () => {
      if (!editorRef.current || !panelRef.current) return;

      const newLines: typeof lines = [];
      const editorRect = editorRef.current.getBoundingClientRect();
      const panelRect = panelRef.current.getBoundingClientRect();

      comments.forEach((comment) => {
        if (comment.isGeneral || comment.textSelections.length === 0) return;

        // Find the selection element in the editor
        const selectionEl = editorRef.current?.querySelector(
          `[data-comment-id="${comment.id}"]`
        ) as HTMLElement;

        // Find the comment card in the panel
        const commentCard = panelRef.current?.querySelector(
          `[data-comment-card-id="${comment.id}"]`
        ) as HTMLElement;

        // Find the numbered badge in the comment card
        const numberBadge = commentCard?.querySelector(
          `.absolute.left-2.top-3`
        ) as HTMLElement;

        if (selectionEl && commentCard && numberBadge) {
          const selRect = selectionEl.getBoundingClientRect();
          const badgeRect = numberBadge.getBoundingClientRect();

          // Connect from center of comment badge to center of selection badge
          const x1 = badgeRect.left + badgeRect.width / 2;
          const y1 = badgeRect.top + badgeRect.height / 2;
          
          // Find the number badge in the selection highlight
          const selNumberBadge = selectionEl.querySelector('.inline-flex.items-center.justify-center') as HTMLElement;
          const selNumberRect = selNumberBadge?.getBoundingClientRect();
          
          if (!selNumberRect) return; // Skip if badge not found
          
          const x2 = selNumberRect.left + selNumberRect.width / 2;
          const y2 = selNumberRect.top + selNumberRect.height / 2;

          newLines.push({
            x1,
            y1,
            x2,
            y2,
            color: comment.color,
          });
        }
      });

      setLines(newLines);
    };

    // Calculate on mount and when dependencies change
    calculateLines();

    // Recalculate on scroll and resize
    const handleUpdate = () => {
      requestAnimationFrame(calculateLines);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Use ResizeObserver for more accurate updates
    const resizeObserver = new ResizeObserver(handleUpdate);
    if (editorRef.current) resizeObserver.observe(editorRef.current);
    if (panelRef.current) resizeObserver.observe(panelRef.current);

    // Add MutationObserver to watch for content changes
    const mutationObserver = new MutationObserver(handleUpdate);
    if (editorRef.current) {
      mutationObserver.observe(editorRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [comments, editorRef, panelRef]);

  const colorMap: Record<string, string> = {
    blue: '#3b82f6',
    amber: '#f59e0b',
    orange: '#f97316',
    purple: '#a855f7',
    green: '#22c55e',
    pink: '#ec4899',
    teal: '#14b8a6',
  };

  if (lines.length === 0) return null;

  return (
    <svg
      className="fixed inset-0 pointer-events-none z-10"
      style={{ width: '100vw', height: '100vh' }}
    >
      <defs>
        {Object.entries(colorMap).map(([name, color]) => (
          <filter key={name} id={`shadow-${name}`}>
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        ))}
      </defs>
      {lines.map((line, idx) => {
        const midX = (line.x1 + line.x2) / 2;
        const color = colorMap[line.color] || colorMap.blue;
        
        // Create a curved Bézier path
        const path = `M ${line.x1} ${line.y1} C ${midX} ${line.y1}, ${midX} ${line.y2}, ${line.x2} ${line.y2}`;

        return (
          <path
            key={idx}
            d={path}
            stroke={color}
            strokeWidth="2"
            fill="none"
            opacity="0.6"
            filter={`url(#shadow-${line.color})`}
          />
        );
      })}
    </svg>
  );
};
